import { z } from 'zod';
// The tickets vocabulary — the platform words of the operational work record
// (concept: ems-domain DOMAIN-RULES §7). Classes are the stable concept axis;
// TYPES are content with app-wide meaning (a type binds behavior — fields,
// checklists, steps — declaratively, never a schema CHECK, never code branches):
// the lists are cut at the ticketing wave from the ops teams' vocabulary, and
// v1 carries only what the capture slice writes.
export const TICKET_CLASSES = ['capture', 'request', 'work_order'];
export const TicketClassSchema = z.enum(TICKET_CLASSES);
/** Per-class type lists — the ONE list the request schemas derive their type
 *  enums from (requests.ts; add a type here and the door accepts it). Only the
 *  capture class is creatable in v1; the request and work-order lists stay
 *  EMPTY until the ticketing wave cuts them (the legacy candidates live in
 *  ems-domain GLOSSARY's vocabulary preview). The capture type word is
 *  `media` — the visit's photos, videos and voice notes (EN Media · FR Médias
 *  · AR وسائط); a visit is never a type (it is only ever the result of a
 *  check-in). */
export const TICKET_TYPES_BY_CLASS = {
    capture: ['media'],
    request: [],
    work_order: [],
};
/** The one capture type of v1 (the container's word). */
export const CAPTURE_TYPE = 'media';
// ── the lifecycle (ADR-073) ───────────────────────────────────────────────────
/** The five cross-class PHASES — whose court the ball is in; the same for every
 *  class; a class may skip one, never add one. Cross-class readers (the inbox,
 *  the open-work pool, the bot's "your open work", the widget) read the phase
 *  only. The code key `in_progress` prints as "Open" across classes and as a
 *  class's own stage word inside one. */
export const TICKET_PHASES = ['in_progress', 'pending', 'completed', 'closed', 'cancelled'];
/** The CAPTURE class's stages — the class owns its stage set; a type walks a
 *  path through it. Decided by ONE function from three facts, in this order:
 *  closed (closed always wins) → any photo waiting for a review answer =
 *  to_review → the visit still open = in_progress → else completed. Never
 *  cancelled: something was captured. The request and work-order sets arrive
 *  with their phases (and widen the DB CHECK — a stage is the machine). */
export const CAPTURE_STAGES = ['in_progress', 'to_review', 'completed', 'closed'];
export const CaptureStageSchema = z.enum(CAPTURE_STAGES);
/** The OPEN stages — where someone still owes an act (the dormancy
 *  predicate's population). Completed is finished and only waits for the
 *  grace; closed is filed. */
export const CAPTURE_OPEN_STAGES = ['in_progress', 'to_review'];
/** The hub's DEFAULT population — open work: everything but closed; the filed
 *  rows join it only through the Closed chip's WINDOW on the closing time
 *  (ADR-073 §7 · ADR-078 §2). */
export const CAPTURE_DEFAULT_LENS = ['in_progress', 'to_review', 'completed'];
/** A bulk act over "the rows in view" selects on the SERVER by the page's
 *  filters (ADR-078 §4): at most this many per act, the remainder named
 *  ("1,000 closed, 240 left"); both sides read it. */
export const TICKET_BATCH_CLOSE_MAX = 1000;
/** A stage's cross-class phase. */
export const STAGE_PHASE = {
    in_progress: 'in_progress',
    to_review: 'in_progress',
    completed: 'completed',
    closed: 'closed',
};
/** The lifecycle EVENT vocabulary — the CAUSE a lifecycle line carries, past
 *  tense, one word each, the same key across classes (the printed sentence is
 *  per class). Ruled for captures; challengeable with the ceremony (an ADR line
 *  · the exhaustive table test · the named refusals) when other classes land. */
export const LIFECYCLE_EVENTS = [
    'created', // the container minted (the first photo of a visit)
    'media_landed', // a photo committed
    'review_answered', // a review answer on one of its photos (single or bulk)
    'file_removed', // a photo deleted or purged
    'file_restored', // a photo restored from the recycle bin
    'visit_ended', // check-out · superseded · expired (effective_at = the presumed end when expired)
    'visit_rechecked', // a same-site re-check-in refreshed the verdict
    'grace_elapsed', // the grace sweep filed it
    'closed', // a manage holder filed it
    'reopened', // a manage holder reopened it — or a late photo landed in a closed container
    'policy_rejudged', // the project's review policy / trust dial changed, or the nightly pass found drift
];
export const LifecycleEventSchema = z.enum(LIFECYCLE_EVENTS);
/** How a visit ended. `expired` is the sweep's presumed end ("check-out not
 *  recorded" — checked_out_at = checked_in_at + the project's cap). */
export const VISIT_CLOSED_REASONS = ['checked_out', 'superseded', 'expired'];
export const VisitClosedReasonSchema = z.enum(VISIT_CLOSED_REASONS);
/** The journal's entry kinds — what the capture lifecycle writes: `observation`
 *  (a photo — the capture's link to its file) · `visit` (the EDGE: one line per
 *  ticket × visit, for every class) · `lifecycle` (a stage move with its cause
 *  and two clocks). The rest of the taxonomy (comment · action · external ·
 *  cmdb_impact · reading) is re-cut at the ticketing wave's genesis — the same
 *  law as the type lists. */
export const JOURNAL_KINDS = ['observation', 'visit', 'lifecycle'];
/** What the bot can capture (the bot's capture media kinds). */
export const CAPTURE_KINDS = ['photo', 'video', 'voice'];
export const CaptureKindSchema = z.enum(CAPTURE_KINDS);
/** The position REASON a capture under a visit carries under the project's
 *  review policy — the two the files review vocabulary (files/review.ts) can
 *  trace to a VISIT: away from the site, or no position at all. Computed at
 *  read, never stored; a channel speaks it as the consequence ("photos will
 *  need review"). */
export const VISIT_POSITION_REVIEW = ['off_site', 'no_position'];
export const VisitPositionReviewSchema = z.enum(VISIT_POSITION_REVIEW);
// ── the hub's lenses ─────────────────────────────────────────────────────────
/** The FACT chips — an operational situation at TICKET grain, each a stored
 *  column (`visit_open`), a range on the indexed activity stamp (dormant), or
 *  the person's own follows (never a probe — ADR-078 §1), shown only when the
 *  case exists, in the reach order (the chips that owe an act lead; the
 *  personal lens trails). A visit-grain fact (away · no position) is the
 *  visits log's, never a hub chip. */
export const TICKET_FACTS = ['dormant', 'checked_in_now', 'followed'];
export const TicketFactSchema = z.enum(TICKET_FACTS);
/** The list's group-by axes (ADR-078 §4 · ADR-040 §6): the server answers the
 *  bands with their true counts and the opened bands' rows as ONE stream, 100
 *  a page. Recency bands by the last activity on the calendar ladder below;
 *  person by who sent it (the data's own id, the name as the label). A site
 *  is a thousands-valued axis — a FILTER, never a band. */
export const TICKET_GROUP_BY = ['recency', 'person'];
/** The hub's rungs of the calendar ladder (common/bands — ADR-082 §4's words):
 *  the open work sorted by freshness; two weeks untouched is stale whatever the
 *  month (the Dormant chip and the Created filter cut the pile), so the month
 *  and year rungs stay off. Cut in the screen's zone (`tz` rides with
 *  `group_by=recency`) on the project's week; empty rungs are not listed. */
export const TICKET_RECENCY_BANDS = ['today', 'yesterday', 'this_week', 'previous_week', 'older'];
/** The list's sort keys; the DIRECTION rides beside them (`sort_dir`), each key
 *  carrying its useful default — activity and creation newest first, the waiting
 *  number biggest first, a site's name and the stage rank ascending. */
export const TICKET_SORT_KEYS = ['last_activity', 'site', 'stage', 'waiting', 'created'];
export const TICKET_SORT_DEFAULT_DIR = { last_activity: 'desc', site: 'asc', stage: 'asc', waiting: 'desc', created: 'desc' };
/** The Field work settings' bounds (mirrors the baseline CHECKs). */
export const TICKET_GRACE_DAYS_MAX = 365;
export const TICKET_DORMANT_HOURS_MAX = 720;
